import { type WheelEvent } from 'react';

import type {
  DragEndProps,
  DragMoveProps,
  DragStartProps,
  PointerEventModifiers,
  PointerEventPosition,
} from '../../../types/pointer-events.js';
import {
  exploreDragEnd,
  exploreDragMove,
  exploreDragStart,
} from '../interactions/explore/dragInteractions.js';
import {
  exploreKeyboardEnter,
  exploreKeyboardEscape,
} from '../interactions/explore/keyboardInteractions.js';
import {
  exploreMouseClickOrTap,
  exploreMouseHover,
  exploreMouseWheel,
} from '../interactions/explore/mouseInteractions.js';
import {
  panDragEndWithMiddleButton,
  panDragMoveWithMiddleButton,
  panDragStartWithMiddleButton,
} from '../interactions/pan/dragInteractions.js';
import {
  zoomKeyboardDown,
  zoomKeyboardUp,
} from '../interactions/zoom/keyboardInteractions.js';
import type {
  exploreInteractions,
  InteractionsHandlers,
  InteractionTriggers,
} from '../types.js';
import { reset } from './reset.js';

export const getExploreInteractions = ({
  selectionArea,
  eventCancelled,
  onSelectEnd,
  lastMousePositionRef,
  pinned,
  onHover,
  onTogglePin,
  onModeChange,
  setCursorMode,
  height,
  onSelectChange,
  onSelectStart,
  onReset,
  width,
  onZoomChange,
  isZoomXDisabled,
  onPanChange,
  panDeltaRef,
  onPanStart,
  onPanEnd,
}: exploreInteractions): InteractionsHandlers => {
  return {
    onMouseHover: (event: PointerEventPosition & PointerEventModifiers) =>
      exploreMouseHover({
        event,
        pinned,
        selectionArea,
        lastMousePositionRef,
        setCursorMode,
        onHover,
      }),
    onMouseClickOrTap: (event: PointerEventPosition & PointerEventModifiers) =>
      exploreMouseClickOrTap({
        event,
        selectionArea,
        onHover,
        onTogglePin,
        pinned,
        onSelectEnd,
        lastMousePositionRef,
      }),
    onDragStart: (event: DragStartProps & PointerEventModifiers) =>
      exploreDragStart({ event, onSelectStart }),
    onDragMove: (event: DragMoveProps & PointerEventModifiers) =>
      exploreDragMove({ event, height, width, onSelectChange }),
    onDragEnd: (event: DragEndProps & PointerEventModifiers) =>
      exploreDragEnd({
        event,
        height,
        width,
        onSelectEnd,
        lastMousePositionRef,
      }),
    onDoubleClickOrTap: (triggeredBy: InteractionTriggers) => {
      const mousePosition = lastMousePositionRef.current;

      onReset?.({
        triggeredBy,
        mousePosition,
      });

      reset(lastMousePositionRef);
    },
    onWheel: (event: WheelEvent) => {
      exploreMouseWheel({ event, onPanChange });
    },
    onKeyEscape: () =>
      exploreKeyboardEscape({
        selectionArea,
        eventCancelled,
        onSelectEnd,
        lastMousePositionRef,
        pinned,
        onHover,
        onTogglePin,
        onModeChange,
      }),
    onKeyEnter: () =>
      exploreKeyboardEnter({
        selectionArea,
        lastMousePositionRef,
        width,
        onSelectEnd,
        onZoomChange,
        onHover,
        isZoomXDisabled,
      }),
    onKeyRight: () => {
      return;
    },
    onKeyLeft: () => {
      return;
    },
    onKeyUp: (isTemporaryZoomMode: boolean) => {
      if (isTemporaryZoomMode) {
        zoomKeyboardUp({ width, onZoomChange });
      }
    },
    onKeyDown: (isTemporaryZoomMode: boolean) => {
      if (isTemporaryZoomMode) {
        zoomKeyboardDown({ width, onZoomChange });
      }
    },
    onDragStartWithMiddleButton: () =>
      panDragStartWithMiddleButton({ panDeltaRef, onPanStart }),
    onDragMoveWithMiddleButton: (
      event: DragMoveProps & PointerEventModifiers,
    ) => panDragMoveWithMiddleButton({ event, panDeltaRef, onPanChange }),
    onDragEndWithMiddleButton: (event: DragEndProps & PointerEventModifiers) =>
      panDragEndWithMiddleButton({
        event,
        panDeltaRef,
        lastMousePositionRef,
        onPanEnd,
      }),
  };
};
